import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { DigitalChecklistStatus, IDealerInfo, IDigitalChecklist, IResponse } from "@signal/asp-data-commons";
import { Observable, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, withLatestFrom } from "rxjs/operators";
import { AlertService } from "../../shared/services/alert.service";
import { DeliveryChecklistService } from "../../shared/services/delivery-checklist.service";
import { GLOBAL_CONSTANT } from "../../shared/services/util.service";
import { toData } from "../app.adapter";
import { DealerState } from "../dealer/dealer.reducer";
import { dealerDetailsData } from "../dealer/dealer.selectors";
import * as fromActions from '../delivery-checklist/delivery-checklist.actions';
import { ServiceAppointmentState } from "../service-appointment/service-appointment.reducer";
import { createDigitalChecklistRequest, updateDigitalChecklistRequest } from "./delivery-checklist.adapter";
import { DeliveryChecklistState } from "./delivery-checklist.reducer";
import { stateDetails } from "./delivery-checklist.selectors";
import * as fromServiceActions from '../../store/service-appointment/service-appointment.actions';
import _ from 'lodash';

@Injectable()
export class DeliveryChecklistEffects {
    somethingWentWrongPleaseTryAgain = 'otherTexts.somethingWentWrongPleaseTryAgain';
    deliveryChecklstDeleted = 'deliveryChecklist.checklistDeleted';
    emailSent = 'deliveryChecklist.emailSent';
    checklistAlreadyCreatedMsg = 'deliveryChecklist.checklistAlreadyCreatedMsg';

    constructor(
        private readonly actions$: Actions,
        private readonly alertService: AlertService,
        private readonly deliveryChecklistService: DeliveryChecklistService,
        private readonly store: Store<DeliveryChecklistState>,
        private readonly dealerState: Store<DealerState>,
        private readonly serviceAppointmentState: Store<ServiceAppointmentState>,
    ) { }

    LoadChecklistSummary: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.LoadChecklistSummary>(
                fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST_SUMMARY
            ),
            mergeMap((action: fromActions.LoadChecklistSummary) =>
                this.deliveryChecklistService.getChecklistSummary(action.payload.checklistSummaryStatus,action.payload.pageNumber,action.payload.pageSize,action.payload?.filters).pipe(
                    switchMap((data: IResponse<any>) => toData(data)),
                    map((data: any) => {
                        let key='';
                        const count = data.totalRecords || 0;
                        if(action.payload.checklistSummaryStatus === DigitalChecklistStatus.COMPLETED || action.payload.checklistSummaryStatus === DigitalChecklistStatus.SUBMITTED) {
                            key = 'completedDigitalChecklist'
                            if(_.isEqual(Object.keys(action.payload?.filters || {}), ['sorts']))
                            {
                                this.serviceAppointmentState.dispatch( new fromServiceActions.UpdateNotificationDashboardOnLoad({key, count: count}));
                            }
                        }
                        if(action.payload.checklistSummaryStatus === DigitalChecklistStatus.PENDING) {
                            key = 'pendingDigitalChecklist'
                            if(_.isEqual(Object.keys(action.payload?.filters || {}), ['sorts']))
                            {
                                this.serviceAppointmentState.dispatch( new fromServiceActions.UpdateNotificationDashboardOnLoad({key, count: count}));
                            }
                        }
                        return new fromActions.LoadChecklistSummarySuccess({ checklist:data.records ,totalCount:data.totalRecords,appendData:action.payload.appendData});
                    }),
                    catchError(err => {
                        this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseTryAgain);
                        return of(new fromActions.LoadChecklistSummaryFailure({ error: err }))
                    })
                )
            )
        )
    })

    LoadArchivedChecklists: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.LoadArchivedChecklists>(
                fromActions.DeliveryChecklistActionTypes.LOAD_ARCHIVED_CHECKLISTS
            ),
            mergeMap((action: fromActions.LoadArchivedChecklists) =>
                this.deliveryChecklistService.fetchArchivedChecklists(action.payload.year,action.payload.month, action.payload.pageNumber, action.payload.pageSize,action.payload?.filters).pipe(
                    map((checklistDetails: any) => new fromActions.LoadArchivedChecklistsSuccess({ checklistDetails:checklistDetails.records,totalCount:checklistDetails.totalRecord,year:action.payload.year,month:action.payload.month })),
                    catchError(err => {
                        this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseTryAgain);
                        return of(new fromActions.LoadChecklistFailure({ error: err }))
                    })
                )
            )
        )
    });

    LoadChecklist: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.LoadChecklist>(
                fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST
            ),
            mergeMap((action: fromActions.LoadChecklist) => {
                return this.deliveryChecklistService.getChecklist(action.payload.fuelType).pipe(
                    switchMap((checklist: IResponse<IDigitalChecklist[]>) => toData(checklist)),
                    map((checklist: any) => new fromActions.LoadChecklistSuccess({ checklistDetails: checklist.data })),
                    catchError(err => of(new fromActions.LoadChecklistFailure({ error: err })))
                );
            }
            )
        );
    })

    createDigiChekReq: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.CreateDigiChekReq>(
                fromActions.DeliveryChecklistActionTypes.CREATE_DIGICHECK_REQUEST
            ),
            withLatestFrom(this.store.select(stateDetails), this.dealerState.select(dealerDetailsData)),
            switchMap(([action, state, dealerDetails]: [fromActions.CreateDigiChekReq, DeliveryChecklistState, IDealerInfo]) =>
                this.deliveryChecklistService.createDigiCheckReq(createDigitalChecklistRequest(state, action.payload.isSaveForLater, dealerDetails, action.payload.isVinValid)).pipe(
                    map((checklistData) => new fromActions.CreateDigiChekReqSuccess({ reqModel: checklistData.data })
                    ),
                    catchError(err => {
                        if(err?.error?.status?.code === 500 || err?.status === 500){
                            this.alertService.showAlert(this.checklistAlreadyCreatedMsg);
                        }else {
                            this.alertService.showAlert(GLOBAL_CONSTANT.errorMessage);
                        }
                        return of(new fromActions.CreateDigiChekReqFail({ error: err }))
                    })
                )
            )
        )
    });
    
    resubmitDigiChekReq: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.ResubmitDigiChekReq>(
                fromActions.DeliveryChecklistActionTypes.RESUBMIT_DIGICHECK_REQUEST
            ),
            mergeMap((action: fromActions.ResubmitDigiChekReq) => this.deliveryChecklistService.resubmitDigiCheckReq(action.payload.digitalChecklist).pipe(
                map((data) => {
                    this.alertService.showAlert(data.data.key, '', '', 5000, false);
                    return new fromActions.ResubmitDigiChekReqSuccess({ digitalChecklist: data })}
                ),
                catchError(err => {
                    this.alertService.showAlert(GLOBAL_CONSTANT.errorMessage);
                    return of(new fromActions.ResubmitDigiChekReqFailure({ error: err?.error?.status?.messages[0] || GLOBAL_CONSTANT.errorMessage}))}
                )
            ))
        )
    });
    

    getEditData: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.GetEditData>(
                fromActions.DeliveryChecklistActionTypes.GET_EDIT_DATA
            ),
            mergeMap((action: fromActions.GetEditData) =>
                this.deliveryChecklistService.getEditData(action.payload.digiChekId).pipe(
                    map((checklistModel) => new fromActions.GetEditDataSuccess({ checklistModel: checklistModel.data })),
                    catchError(err => of(new fromActions.GetEditDataFail({ error: err })))
                )));
    })

    editDigiChekReq: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.EditDigiCheckReq>(
                fromActions.DeliveryChecklistActionTypes.EDIT_DIGICHECK_REQUEST
            ),
            withLatestFrom(this.store.select(stateDetails), this.dealerState.select(dealerDetailsData)),
            switchMap(([action, state, dealerDetails]: [fromActions.CreateDigiChekReq, DeliveryChecklistState, IDealerInfo]) =>
                this.deliveryChecklistService.updateDigiChekReq(updateDigitalChecklistRequest(state, action.payload.isSaveForLater, dealerDetails, action.payload.isVinValid)).pipe(
                    map((checklistModel) => {
                        if(checklistModel.data.status === DigitalChecklistStatus.SUBMITTED) {
                            const key = 'pendingDigitalChecklist'
                            this.serviceAppointmentState.dispatch( new fromServiceActions.UpdateNotificationDashboard({key, count: -1}));
                        }
                        return new fromActions.EditDigiCheckReqSuccess({ checklistModel: checklistModel.data })
                    }),
                    catchError(err => {
                        this.alertService.showAlert(GLOBAL_CONSTANT.errorMessage);
                        return of(new fromActions.EditDigiCheckReqFail({ error: err }))
                    })
                )
            )
        );
    })

    cancelDigiChekReq: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.CancelChecklist>(
                fromActions.DeliveryChecklistActionTypes.CANCEL_DIGICHECK
            ),
            mergeMap((action: fromActions.CancelChecklist) =>
                this.deliveryChecklistService.cancelCheckList(action.payload.digitalChecklist).pipe(
                    map(() => {
                        this.alertService.showAlert(this.deliveryChecklstDeleted, '', '', 5000, false);
                        let key='';
                        if(action.payload.digitalChecklist.status === DigitalChecklistStatus.COMPLETED || action.payload.digitalChecklist.status === DigitalChecklistStatus.SUBMITTED) {
                            key = 'completedDigitalChecklist'
                        }
                        if(action.payload.digitalChecklist.status === DigitalChecklistStatus.PENDING) {
                            key = 'pendingDigitalChecklist'
                        }
                        this.serviceAppointmentState.dispatch( new fromServiceActions.UpdateNotificationDashboard({key, count: -1}));
                        return new fromActions.CancelChecklistSuccess({ digitalChecklistId: action.payload.digitalChecklist.digichkId });

                    }
                    ),
                    catchError(err => {
                        this.alertService.showAlert(GLOBAL_CONSTANT.errorMessage);
                        return of(new fromActions.CancelChecklistFailure({ error: err?.error?.status?.messages[0] || GLOBAL_CONSTANT.errorMessage}))}
                    )
                
                )
            )
        );
    });

    sendPdfEmail: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.SendEmail>(
                fromActions.DeliveryChecklistActionTypes.SEND_EMAIL
            ),
            mergeMap((action: fromActions.SendEmail) => {
                const requestPayload = action.payload.emailAddress
                    ? {
                        appointmentIds: [{
                            appointmentId: action.payload.digitalChecklist.digichkId,
                            email: action.payload.emailAddress
                        }]
                    }
                    : { appointmentIds: [action.payload.digitalChecklist.digichkId] };
                return this.deliveryChecklistService.sendChecklistPdf(requestPayload,action.payload.type).pipe(
                    map(() => {
                    this.alertService.showAlert(this.emailSent, '', '', 5000, false);
                    return new fromActions.SendEmailSuccess({ digitalChecklistId: action.payload.digitalChecklist.digichkId })
                    }
                    ),
                    catchError(err => {
                        this.alertService.showAlert(GLOBAL_CONSTANT.errorMessage);
                        return of(new fromActions.SendEmailFailure({ error: err?.error?.status?.messages[0] || GLOBAL_CONSTANT.errorMessage}))}
                    )
                );
            })
        );
    });
}
